import { useCallback, useLayoutEffect, useState } from "react";
import type { CSSProperties, MutableRefObject } from "react";
import { resolveFloatingPosition } from "./floatingUtils";
import type { FloatingPlacement } from "./floatingUtils";

type UseFloatingPositionOptions = {
  active: boolean;
  anchorRef: MutableRefObject<HTMLElement | null>;
  floatingRef: MutableRefObject<HTMLElement | null>;
  placement?: FloatingPlacement;
  offset?: number;
  viewportPadding?: number;
};

type UseFloatingPositionResult = {
  floatingStyle: CSSProperties;
  currentPlacement: FloatingPlacement;
  updatePosition: () => void;
};

const INITIAL_STYLE: CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  visibility: "hidden",
};

export const useFloatingPosition = ({
  active,
  anchorRef,
  floatingRef,
  placement = "bottom-right",
  offset = 8,
  viewportPadding = 16,
}: UseFloatingPositionOptions): UseFloatingPositionResult => {
  const [floatingStyle, setFloatingStyle] = useState<CSSProperties>(INITIAL_STYLE);
  const [currentPlacement, setCurrentPlacement] = useState<FloatingPlacement>(placement);

  const updatePosition = useCallback(() => {
    const anchorElement = anchorRef.current;
    const floatingElement = floatingRef.current;

    if (!anchorElement || !floatingElement) {
      setFloatingStyle(INITIAL_STYLE);
      setCurrentPlacement(placement);
      return;
    }

    const anchorRect = anchorElement.getBoundingClientRect();
    const floatingRect = floatingElement.getBoundingClientRect();
    const nextPosition = resolveFloatingPosition({
      anchorRect,
      floatingRect,
      placement,
      offset,
      viewportPadding,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });

    setCurrentPlacement(nextPosition.placement);
    setFloatingStyle({
      position: "fixed",
      top: nextPosition.top,
      left: nextPosition.left,
      visibility: "visible",
    });
  }, [anchorRef, floatingRef, offset, placement, viewportPadding]);

  useLayoutEffect(() => {
    if (!active) {
      setFloatingStyle(INITIAL_STYLE);
      setCurrentPlacement(placement);
      return;
    }

    let frameId = 0;
    const scheduleUpdate = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(updatePosition);
    };
    const floatingElement = floatingRef.current;

    scheduleUpdate();

    const resizeObserver =
      typeof ResizeObserver !== "undefined" && floatingElement
        ? new ResizeObserver(() => {
            scheduleUpdate();
          })
        : null;

    if (resizeObserver && floatingElement) {
      resizeObserver.observe(floatingElement);
    }

    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("scroll", scheduleUpdate, true);
    window.visualViewport?.addEventListener("resize", scheduleUpdate);
    window.visualViewport?.addEventListener("scroll", scheduleUpdate);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("scroll", scheduleUpdate, true);
      window.visualViewport?.removeEventListener("resize", scheduleUpdate);
      window.visualViewport?.removeEventListener("scroll", scheduleUpdate);
    };
  }, [active, placement, updatePosition]);

  return {
    floatingStyle,
    currentPlacement,
    updatePosition,
  };
};
